import React from 'react';
import {
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';
import { Box } from '@mui/system';
import { Subtopic } from '@shared/domain/subtopic/subtopic.aggregate';
import { Content, ContentKind } from '@shared/domain/content/content.aggregate';
import { Control, Controller } from 'react-hook-form';
import VideoIcon from '@mui/icons-material/PlayCircle';
import GameIcon from '@mui/icons-material/SportsEsports';
import DocumentIcon from '@mui/icons-material/Article';
import { validations } from '@shared/infrastructure/ui/validations';

const SubtopicContentElement = ({
  kind,
  label
}: {
  kind: ContentKind;
  label: string;
}) => {
  const icons = {
    Video: <VideoIcon />,
    Game: <GameIcon />,
    Document: <DocumentIcon />
  };

  return <Chip label={label} icon={icons[kind]} />;
};

export const SubtopicContent = ({
  contents,
  control
}: {
  contents: Content[];
  control: Control<Subtopic, any>;
}) => {
  return (
    <Controller
      name='contents'
      control={control}
      rules={validations.required()}
      render={({ field, fieldState }) => (
        <FormControl>
          <InputLabel error={!!fieldState.error} id='contents-label'>
            Contents
          </InputLabel>
          <Select
            {...field}
            error={!!fieldState.error}
            multiple
            labelId='contents-label'
            sx={{ my: '0.3rem' }}
            input={
              <OutlinedInput error={!!fieldState.error} label='Contents' />
            }
            renderValue={(value) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {value
                  .map((id) => contents.find((c) => c.id === id))
                  .filter((c) => c)
                  .map((content) => (
                    <SubtopicContentElement
                      key={content!.id}
                      kind={content!.kind}
                      label={content!.nameLanguage}
                    />
                  ))}
              </Box>
            )}
          >
            {[...contents]
              .sort((c1, c2) => c1.name.localeCompare(c2.name))
              .map((content) => (
                <MenuItem key={content.id} value={content.id}>
                  <SubtopicContentElement
                    kind={content.kind}
                    label={content.name}
                  />
                </MenuItem>
              ))}
          </Select>
          <FormHelperText error={!!fieldState.error}>
            {fieldState.error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};
